import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LanguageSwitchComponent } from '../../core/language-switch/language-switch.component';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MenuModel } from '../../core/models/menu.model';
import { AuthenticationService } from '../../core/services/authentication.service';
import { TokenStorageService } from '../../core/services/token-storage.service';
import { SortbyPipe } from '../../core/pipes/sort-by.pipe';
import { MenuDataService } from '../../core/services/menu-data.service';
import { ChatbotComponent } from '../chatbot/chatbot.component';
import { environment } from '../../../environments/environment';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { LiveChatNotificationService } from '../../core/services/live-chat-notification.service';
import { TelephonyPluginManifestService } from '../../core/telephony/telephony-plugin-manifest.service';

@Component({
  selector: 'app-main',
  standalone: true,
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatExpansionModule,
    LanguageSwitchComponent,
    HeaderComponent,
    SortbyPipe,
    ChatbotComponent
  ]
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('snav') snav!: MatSidenav;

  isSidenavOpen = true;
  menu: MenuModel[] = [];
  chatbotEnabled = environment.chatbotEnabled;
  liveChatUnread = 0;
  liveChatToast = '';

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
    .pipe(map(r => r.matches), shareReplay());

  private navSub?: Subscription;
  private liveChatSub = new Subscription();
  private liveChatToastTimer: any = null;

  constructor(
    private authService: AuthenticationService,
    private tokenStorage: TokenStorageService,
    private router: Router,
    private menuDataService: MenuDataService,
    private breakpointObserver: BreakpointObserver,
    public liveChatNotifications: LiveChatNotificationService,
    private readonly telephonyManifest: TelephonyPluginManifestService,
  ) {}

  ngOnInit(): void {
    this.menu = this.tokenStorage.getValidFrames();
    void this.refreshTelephonyMenu();
    this.liveChatNotifications.start();
    this.liveChatSub.add(this.liveChatNotifications.unreadCount$.subscribe(count => {
      this.liveChatUnread = count;
    }));
    this.liveChatSub.add(this.liveChatNotifications.toast$.subscribe(message => {
      this.liveChatToast = message;
      if (this.liveChatToastTimer) clearTimeout(this.liveChatToastTimer);
      this.liveChatToastTimer = setTimeout(() => this.liveChatToast = '', 4500);
    }));
  }

  ngAfterViewInit(): void {
    this.navSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.router.url.startsWith('/telephony/messages') || this.router.url.startsWith('/csr/messages')) {
          this.liveChatNotifications.clear();
        }
        this.isHandset$.pipe(take(1)).subscribe(mobile => {
          if (mobile && this.snav?.opened) this.snav.close();
        });
      });
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
    this.liveChatSub.unsubscribe();
    if (this.liveChatToastTimer) clearTimeout(this.liveChatToastTimer);
  }

  trackById(index: number, item: any): number {
    return item.id;
  }

  toggleSubMenu(index: number): void {
    if (this.menuDataService.openedSubMenuIndex === index) {
      this.menuDataService.openedSubMenuIndex = -1;
    } else {
      this.menuDataService.openedSubMenuIndex = index;
    }
  }

  isSubMenuOpen(index: number): boolean {
    return this.menuDataService.openedSubMenuIndex === index;
  }
   // Check if the current route matches any sub-menu route
  isParentMenuActive(subMenu: any[]): boolean {
    return subMenu.some(sm => this.router.isActive(sm.route, false));
  }

  private async refreshTelephonyMenu(): Promise<void> {
    const manifest = await this.telephonyManifest.load();
    if (!manifest) return;
    const role = this.tokenStorage.decodeToken()?.ROLE ?? this.tokenStorage.decodeToken()?.role;
    const telephonyMenu = this.telephonyManifest.toMenu(manifest, role);
    const withoutOldTelephony = (this.menu ?? []).filter(m =>
      m.frame !== 'TELEPHONY_CONTACT_CENTER'
      && m.frame !== 'CSR_CONSOLE'
      && m.frame !== 'SUPERVISOR'
      && m.title !== 'Contact Center'
    );
    this.menu = [...withoutOldTelephony, telephonyMenu].sort((a: any, b: any) => (a.order ?? 999) - (b.order ?? 999));
  }
}
